/** Unsaved-changes navigation guard.
 *
 * The console router is a Data Router (`createBrowserRouter`), so react-router's
 * `useBlocker` is available: it intercepts in-app navigations (link clicks, the
 * Tabs, back/forward) while `when` is true. A separate `beforeunload` listener
 * covers full-page unloads (tab close / reload / external nav). The hook returns
 * a `<ConfirmDialog>` element the caller renders alongside its editor.
 *
 * Most editors get this for free through `EditorSaveBar` (which embeds the
 * hook); call it directly only for custom save UIs (users Profile, TO General)
 * or page-level guards covering several mounted tabs (itineraries).
 *
 * A router supports ONE active blocker — never let two mounted components call
 * this with `when` potentially true at once (pass `guard={false}` to
 * EditorSaveBar when the page owns the guard). */
import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';

interface Options {
  /** Confirm-dialog body. Keep it specific to the resource when you can. */
  message?: string;
  /** When set, navigation WITHIN this path prefix (e.g. the record's tabs at
   *  `/itineraries/12/...`) is not blocked — only leaving the record is. Use
   *  for pages whose tab state survives tab switches (lifted drafts). Editors
   *  whose tabs unmount on switch (the `TabPane` pattern) must NOT set this:
   *  there a tab switch really does discard the form. */
  basePath?: string;
}

export function useUnsavedChangesPrompt(
  when: boolean,
  { message = 'You have unsaved changes. Leaving now will discard them.', basePath }: Options = {},
): {
  /** The confirm dialog to render (null when no navigation is pending). */
  prompt: React.ReactElement | null;
} {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!when) return false;
    if (basePath) {
      const next = nextLocation.pathname;
      return next !== basePath && !next.startsWith(`${basePath}/`);
    }
    return currentLocation.pathname !== nextLocation.pathname;
  });

  // Full-page unloads (close/reload) — the browser shows its own generic prompt.
  useEffect(() => {
    if (!when) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [when]);

  const prompt =
    blocker.state === 'blocked' ? (
      <ConfirmDialog
        open
        title="Discard unsaved changes?"
        message={message}
        confirmLabel="Discard changes"
        destructive
        onConfirm={() => blocker.proceed()}
        onCancel={() => blocker.reset()}
      />
    ) : null;

  return { prompt };
}
